
import React, { useState, useCallback } from 'react';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

export interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  profession: string;
  hearAbout: string;
  emailConsent: boolean;
  textConsent: boolean;
}

interface FormSubmissionHandlerProps {
  onSubmit: (data: FormData) => Promise<void>;
  children: (props: {
    isSubmitting: boolean;
    isSubmitted: boolean;
    error: string | null;
    handleSubmit: (data: FormData) => Promise<void>;
    resetForm: () => void;
  }) => React.ReactNode;
}

const FormSubmissionHandler: React.FC<FormSubmissionHandlerProps> = ({ onSubmit, children }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async (data: FormData) => {
    // Validate required fields
    if (!data.firstName.trim() || !data.lastName.trim() || !data.email.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Validate consent
    if (!data.emailConsent && !data.textConsent) {
      setError('Please consent to at least one form of communication.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(data);
      setIsSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit]);

  const resetForm = useCallback(() => {
    setIsSubmitting(false);
    setIsSubmitted(false);
    setError(null);
  }, []);

  return (
    <>
      {children({ isSubmitting, isSubmitted, error, handleSubmit, resetForm })}
    </>
  );
};

export default FormSubmissionHandler;
